/**
 * PositActiveFilters.tsx
 * 
 * Component to display and manage active filters for the Posits listing.
 * Extracted from MyPosits.tsx to improve modularity and reusability.
 */

import React from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface PositActiveFiltersProps {
  filterType: string;
  filterPrivacy: string;
  searchQuery: string;
  setFilterType: (value: string) => void;
  setFilterPrivacy: (value: string) => void;
  setSearchQuery: (value: string) => void;
  clearFilters: () => void;
}

const PositActiveFilters: React.FC<PositActiveFiltersProps> = ({
  filterType,
  filterPrivacy,
  searchQuery,
  setFilterType,
  setFilterPrivacy,
  setSearchQuery,
  clearFilters,
}) => {
  // Check if any filters are active
  const hasActiveFilters = filterType !== "all" || filterPrivacy !== "all" || searchQuery.trim() !== "";
  
  // Don't render if no filters are active
  if (!hasActiveFilters) return null;
  
  // Format filter type for display by converting snake_case to Title Case
  const formatFilterType = (type: string): string => {
    if (type === "all") return type;
    return type.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 my-4">
      <div className="text-sm text-muted-foreground mr-1">Active filters:</div>
      
      {filterType !== "all" && (
        <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
          Type: {formatFilterType(filterType)}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
            onClick={() => setFilterType("all")}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Remove type filter</span>
          </Button>
        </Badge>
      )}
      
      {filterPrivacy !== "all" && (
        <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
          Privacy: {filterPrivacy.charAt(0).toUpperCase() + filterPrivacy.slice(1)}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
            onClick={() => setFilterPrivacy("all")}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Remove privacy filter</span>
          </Button>
        </Badge>
      )}
      
      {searchQuery.trim() !== "" && (
        <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
          Search: {searchQuery}
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
            onClick={() => setSearchQuery("")}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Clear search</span>
          </Button>
        </Badge>
      )}
      
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={clearFilters}
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default PositActiveFilters; 